/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { useEffect } from 'react';

import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { ICheckoutOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management';

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {channelReferenceId}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    pickupDateTimeslot,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<ICheckoutOrderConfirmationViewProps> = (props) => {
    const {
        moduleProps,
        heading,
        backToShoppingLink,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups
    } = props;

    useEffect(() => {
        if (!orderInfomation || !orderSummary) return;
        // @ts-ignore
        const transactionId = document.querySelector('.ms-order-confirmation__order-information-channel-reference-id span').innerText;;
        // @ts-ignore
        const currency = moduleProps?.moduleProps?.data.customerInformation?.result.CurrencyCode;
        // @ts-ignore
        const value = orderSummary?.totalAmount.props.value;
        // @ts-ignore
        const tax = orderSummary?.lines?.taxAmount.props.price;
        // @ts-ignore
        const shipping = orderSummary?.lines?.shippingAmount?.props.price || 0;
        // @ts-ignore
        const coupon = orderSummary?.lines?.totalDiscounts?.props.id || '';

        // @ts-ignore
        const items = groups?.groups[0].salesLines?.map((lineItem, index) => {
            const product = lineItem?.data?.product;
            // @ts-ignore
            const categories = product?.ExtensionProperties?.categories || [];

            return {
                item_id: product?.RecordId?.toString(),
                item_name: product?.Name,
                // @ts-ignore
                discount: product?.DiscountAmount || 0,
                index,
                item_category: (categories && categories[0]) || '',
                item_category2: (categories && categories[1]) || '',
                item_category3: (categories && categories[2]) || '',
                item_category4: (categories && categories[3]) || '',
                item_list_id: product?.ItemId || '',
                item_list_name: (categories && categories[0]) || '',
                price: product?.Price,
                // @ts-ignore
                quantity: lineItem.quantity
            };
        });

        const existingTransaction = window.dataLayer?.some((item: any) => item?.ecommerce?.transaction_id === transactionId);

        if (existingTransaction) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null }); // Clear previous
        window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
                transaction_id: transactionId,
                value,
                tax,
                shipping,
                currency,
                coupon,
                items
            }
        });
    }, [orderInfomation, orderSummary]);
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
